import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, fileUrl, API } from "@/lib/api";
import { haptic, shareOrCopy } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Heart, MessageCircle, ShoppingBag, Star, ShieldCheck, Truck, Recycle, ChevronLeft, Gavel, Flag, Share2 } from "lucide-react";
import StartAuctionModal from "@/components/StartAuctionModal";
import ReportDialog from "@/components/ReportDialog";

const conditionLabel = {
  neuf_etiquette: "Neuf avec étiquette",
  tres_bon: "Très bon état",
  bon: "Bon état",
  correct: "État correct",
};

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [related, setRelated] = useState([]);
  const [showAuctionModal, setShowAuctionModal] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [shipInfo, setShipInfo] = useState(null);

  useEffect(() => {
    api.get("/shipping/fee").then(r => setShipInfo(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (id && !sessionStorage.getItem(`fripri_viewed_${id}`)) {
      sessionStorage.setItem(`fripri_viewed_${id}`, "1");
      api.post(`/products/${id}/view`).catch(() => {});
    }
    api.get(`/products/${id}`).then(r => {
      setProduct(r.data);
      api.get(`/products?category=${r.data.category}&limit=8`).then(rr =>
        setRelated(rr.data.filter(p => p.product_id !== r.data.product_id).slice(0, 4))
      ).catch(()=>{});
    }).catch(()=>{ toast.error("Produit introuvable"); navigate("/catalogue"); });
  }, [id, navigate]);

  if (!product) return <div className="p-20 text-center text-2xl font-black">Chargement…</div>;

  const requireAuth = () => {
    if (!user) { toast.error("Connecte-toi d'abord"); navigate("/login"); return false; }
    return true;
  };

  const addToCart = async () => {
    if (!requireAuth()) return;
    if (product.seller_id === user.user_id) { toast.error("C'est ton propre article"); return; }
    haptic();
    try { await api.post(`/cart/${product.product_id}`); toast.success("Ajouté au panier"); }
    catch { toast.error("Erreur"); }
  };

  const addWishlist = async () => {
    if (!requireAuth()) return;
    haptic();
    try { await api.post(`/wishlist/${product.product_id}`); toast.success("Ajouté aux favoris"); }
    catch { toast.error("Erreur"); }
  };

  const shareProduct = async () => {
    const url = `${API}/p/${product.product_id}`;
    const text = `${product.title} — ${product.price?.toFixed(0)}€ sur Fripri 💚`;
    const res = await shareOrCopy({ title: product.title, text, url });
    if (res === "copied") toast.success("Lien copié ! Colle-le où tu veux 📋");
    else if (res === "failed") toast.error("Impossible de copier le lien");
  };

  const contactSeller = async () => {
    if (!requireAuth()) return;
    navigate(`/messages?to=${product.seller_id}&product=${product.product_id}`);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6 lg:py-10 fade-in has-bottom-nav" data-testid="product-detail">
      <Link to="/catalogue" className="label font-bold inline-flex items-center gap-1 mb-6"><ChevronLeft size={14}/> Retour</Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        {/* Images */}
        <div className="lg:col-span-7">
          <div className="aspect-square overflow-hidden mb-3 bg-[color:var(--color-surface)] border-2 border-black">
            {product.images?.[activeImg] ? (
              <img src={fileUrl(product.images[activeImg])} alt={product.title} className="w-full h-full object-cover" />
            ) : <div className="w-full h-full flex items-center justify-center label">Pas d'image</div>}
          </div>
          {(product.images?.length || 0) > 1 && (
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {product.images.map((img, i) => (
                <button key={i} onClick={()=>setActiveImg(i)} className="w-20 h-20 shrink-0 overflow-hidden border-2"
                  style={{borderColor: i===activeImg ? "var(--color-text)" : "transparent"}}
                  data-testid={`thumb-${i}`}
                >
                  <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:col-span-5">
          <div className="flex items-start gap-2 mb-3">
            <span className="pill">{conditionLabel[product.condition]}</span>
            {product.status === "sold" && <span className="pill pill-coral">Vendu</span>}
          </div>
          <p className="label" data-testid="product-brand">{product.brand || "Sans marque"}</p>
          <h1 className="text-3xl lg:text-4xl font-black tracking-tighter mt-2 mb-4" data-testid="product-title">{product.title}</h1>

          <div className="flex items-baseline gap-3 mb-3">
            <span className="display-num text-4xl lg:text-5xl font-black" style={{color: "var(--color-coral)"}} data-testid="product-price">{product.price?.toFixed(0)}€</span>
            {(() => {
              const pFee = shipInfo?.fees?.[product.package_size || "small"] || 0;
              if (!shipInfo?.enabled || pFee <= 0) return <span className="label text-[color:var(--color-text-muted)]">livraison à part</span>;
              if (product.price >= shipInfo.free_from) return null;
              return <span className="label text-[color:var(--color-text-muted)]">+ {pFee.toFixed(2)}€ de port</span>;
            })()}
          </div>

          {shipInfo?.enabled && (shipInfo.fees?.[product.package_size || "small"] || 0) > 0 && (
            product.price >= shipInfo.free_from ? (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[color:var(--color-mint)] border-2 border-black text-xs font-black uppercase tracking-wider mb-6" data-testid="free-shipping-badge">
                <Truck size={14} strokeWidth={2.5}/> Livraison offerte 💚
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[color:var(--color-yellow)] border-2 border-black text-xs font-bold mb-6" data-testid="free-shipping-nudge-product">
                <Truck size={14} strokeWidth={2.5}/> Livraison offerte dès {shipInfo.free_from.toFixed(0)}€ d'achat — complète ton panier !
              </div>
            )
          )}

          <div className="grid grid-cols-2 gap-3 mb-6">
            {product.size && (
              <div className="border-2 border-black p-3">
                <p className="label mb-1">Taille</p>
                <p className="font-bold">{product.size}</p>
              </div>
            )}
            {product.color && (
              <div className="border-2 border-black p-3">
                <p className="label mb-1">Couleur</p>
                <p className="font-bold">{product.color}</p>
              </div>
            )}
            <div className="border-2 border-black p-3">
              <p className="label mb-1">Catégorie</p>
              <p className="font-bold capitalize">{product.category}</p>
            </div>
            <div className="border-2 border-black p-3">
              <p className="label mb-1">État</p>
              <p className="font-bold">{conditionLabel[product.condition]}</p>
            </div>
          </div>

          <div className="mb-6">
            <p className="label font-bold mb-2">Description</p>
            <p className="text-sm leading-relaxed whitespace-pre-line text-[color:var(--color-text-muted)]" data-testid="product-description">{product.description}</p>
          </div>

          {/* Seller card */}
          <Link to={`/vendeur/${product.seller_id}`} className="card border-2 border-black p-4 flex items-center gap-4 mb-6 card-hover" data-testid="seller-card">
            <div className="w-12 h-12 bg-black text-white flex items-center justify-center font-black text-lg">
              {product.seller_picture ? <img src={product.seller_picture} className="w-full h-full object-cover" alt="" /> : (product.seller_name?.[0] || "U")}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate flex items-center gap-2">
                {product.seller_name}
                {product.seller_is_pro && <span className="text-[10px] font-black bg-[color:var(--color-mint)] border border-black px-1 shrink-0" data-testid="seller-pro-badge">PRO ✓</span>}
              </p>
              <div className="flex items-center gap-1 mt-0.5">
                <Star size={12} fill="#FFE600" strokeWidth={0} />
                <span className="text-xs font-medium">{product.seller_rating?.toFixed(1) || "Nouveau"} {product.seller_review_count ? `· ${product.seller_review_count} avis` : ""}</span>
              </div>
            </div>
            <span className="label">→</span>
          </Link>

          {/* CTA (desktop) */}
          {product.status === "active" && user && product.seller_id === user.user_id && (
            <button onClick={()=>setShowAuctionModal(true)} className="btn btn-mint w-full mb-3" data-testid="start-auction-btn">
              <Gavel size={16} strokeWidth={2.5}/> Mettre aux enchères
            </button>
          )}
          {product.status === "auction" && (
            <Link to={`/encheres/${product.auction_id || ""}`} className="btn btn-mint w-full mb-3 inline-flex items-center justify-center gap-2" data-testid="goto-auction-btn">
              <Gavel size={16} strokeWidth={2.5}/> Voir l'enchère en cours
            </Link>
          )}
          {product.status === "active" && (!user || product.seller_id !== user.user_id) && (
            <div className="hidden md:block space-y-3">
              <button className="btn btn-primary w-full" onClick={addToCart} data-testid="add-to-cart-btn">
                <ShoppingBag size={16} strokeWidth={2.5} /> Ajouter au panier
              </button>
              <div className="grid grid-cols-3 gap-3">
                <button className="btn btn-ghost" onClick={addWishlist} data-testid="add-wishlist-btn">
                  <Heart size={16} strokeWidth={2.5} /> Favoris
                </button>
                <button className="btn btn-ghost" onClick={contactSeller} data-testid="contact-seller-btn">
                  <MessageCircle size={16} strokeWidth={2.5} /> Contacter
                </button>
                <button className="btn btn-ghost" onClick={shareProduct} data-testid="share-product-btn">
                  <Share2 size={16} strokeWidth={2.5} /> Partager
                </button>
              </div>
            </div>
          )}
          {!(product.status === "active" && (!user || product.seller_id !== user.user_id)) && (
            <button className="btn btn-ghost w-full mb-3" onClick={shareProduct} data-testid="share-product-btn-owner">
              <Share2 size={16} strokeWidth={2.5} /> Partager cette annonce
            </button>
          )}

          <div className="mt-6 grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-[color:var(--color-surface)]">
              <ShieldCheck size={20} strokeWidth={2} className="mx-auto mb-1.5" />
              <span className="text-[10px] uppercase tracking-wider font-bold">Sécurisé</span>
            </div>
            <div className="text-center p-3 bg-[color:var(--color-surface)]">
              <Truck size={20} strokeWidth={2} className="mx-auto mb-1.5" />
              <span className="text-[10px] uppercase tracking-wider font-bold">Livraison</span>
            </div>
            <div className="text-center p-3 bg-[color:var(--color-mint)]">
              <Recycle size={20} strokeWidth={2} className="mx-auto mb-1.5" />
              <span className="text-[10px] uppercase tracking-wider font-bold">Éco</span>
            </div>
          </div>

          {user && product.seller_id !== user.user_id && (
            <button onClick={()=>setShowReport(true)} className="mt-4 text-xs text-[color:var(--color-text-muted)] inline-flex items-center gap-1.5 hover:text-[color:var(--color-coral)] font-bold uppercase tracking-wider" data-testid="report-product-btn">
              <Flag size={12}/> Signaler cette annonce
            </button>
          )}
        </div>
      </div>

      {/* Mobile sticky CTA */}
      {product.status === "active" && (
        <div className="mobile-sticky-cta md:hidden flex gap-2" data-testid="mobile-cta-bar">
          <button onClick={shareProduct} className="btn btn-ghost px-3" aria-label="Partager" data-testid="mobile-share-btn"><Share2 size={18}/></button>
          <button onClick={addWishlist} className="btn btn-ghost px-3" aria-label="Favoris"><Heart size={18}/></button>
          <button onClick={contactSeller} className="btn btn-ghost px-3" aria-label="Contacter"><MessageCircle size={18}/></button>
          <button onClick={addToCart} className="btn btn-primary flex-1" data-testid="mobile-add-to-cart">
            <ShoppingBag size={16}/> Acheter — {product.price?.toFixed(0)}€
          </button>
        </div>
      )}

      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="text-2xl lg:text-3xl font-black tracking-tighter mb-6">Tu pourrais aimer</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {related.map(p => (
              <Link key={p.product_id} to={`/produit/${p.product_id}`} className="block product-card">
                <div className="aspect-square overflow-hidden product-img-wrap mb-2">
                  {p.images?.[0] && <img src={fileUrl(p.images[0])} alt={p.title} className="product-img w-full h-full object-cover" />}
                </div>
                <p className="label">{p.brand || "—"}</p>
                <h3 className="font-bold text-sm truncate">{p.title}</h3>
                <p className="font-black" style={{color:"var(--color-coral)"}}>{p.price?.toFixed(0)}€</p>
              </Link>
            ))}
          </div>
        </section>
      )}
      {showAuctionModal && (
        <StartAuctionModal
          productId={product.product_id}
          productTitle={product.title}
          onClose={()=>setShowAuctionModal(false)}
        />
      )}
      {showReport && (
        <ReportDialog targetType="product" targetId={product.product_id} targetLabel={product.title} onClose={()=>setShowReport(false)}/>
      )}
    </div>
  );
}
